/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { getPublicSiteBasePath } from "@cosmos/common-utils";
import { useReportWebVitals } from "next/web-vitals";
import { useEffect } from "react";

const FLUSH_DELAY_MS = 5_000;

/**
 * Builds the metrics endpoint URL for the current site.
 * @param basePath - Optional override (for the unified app, where NEXT_PUBLIC_* can't vary per-site)
 */
export function getMetricsEndpoint(basePath?: string): string {
	return `${getPublicSiteBasePath(basePath)}/api/cosmos/metrics`;
}

interface BufferedMetric {
	name: string;
	value: number;
	route: string;
}

const buffer: BufferedMetric[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;

function flush(endpoint: string) {
	clearTimeout(timer);
	timer = undefined;
	const entries = buffer.splice(0);
	if (entries.length === 0) return;
	const payload = new Blob([JSON.stringify(entries)], { type: "application/json" });
	try {
		if (typeof navigator.sendBeacon === "function") {
			navigator.sendBeacon(endpoint, payload);
		} else {
			fetch(endpoint, { method: "POST", body: payload, keepalive: true }).catch(() => {
				/* noop */
			});
		}
	} catch {
		// Best-effort; never block the UI
	}
}

function scheduleFlush(endpoint: string) {
	if (!timer) {
		timer = setTimeout(() => flush(endpoint), FLUSH_DELAY_MS);
	}
}

export function MetricsReporter({ basePath }: { basePath?: string } = {}) {
	const endpoint = getMetricsEndpoint(basePath);
	useEffect(() => {
		const onVisChange = () => {
			if (document.visibilityState === "hidden") flush(endpoint);
		};
		document.addEventListener("visibilitychange", onVisChange);
		return () => {
			document.removeEventListener("visibilitychange", onVisChange);
			flush(endpoint);
		};
	}, [endpoint]);

	useReportWebVitals((metric) => {
		buffer.push({ name: metric.name, value: metric.value, route: window.location.pathname });
		scheduleFlush(endpoint);
	});

	return null;
}
